/**
 * Utilidades geográficas compartidas.
 *
 * `haversineMeters` — extraído 2026-07-23 (plan `rv-mapa-gps-tiempo-real`,
 * Task B2) de `useSurveillanceRouteGpsTracking.ts` para reusarlo también en
 * `pkFromMesh.ts` (fallback de PK offline). Espejo del helper del backend
 * (`geoPositionRoad.service.ts`) — mismo algoritmo, misma fórmula.
 */
export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Rumbo inicial del trayecto A→B, en grados [0,360), 0 = norte, horario
 * (fórmula estándar de rumbo ortodrómico).
 *
 * Lo consume la señal S2 del detector de cambio de sentido: comparar el rumbo
 * real de la patrulla con el del eje de la carretera es el criterio clásico del
 * map-matching, y es lo único que caza que la malla haya mentido con el PK.
 */
export function bearingDegrees(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);
  const deltaLng = toRad(lng2 - lng1);
  const y = Math.sin(deltaLng) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLng);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/** Diferencia mínima entre dos rumbos, en grados [0,180]. */
export function angleDiffDegrees(a: number, b: number): number {
  const normalize = (deg: number) => ((deg % 360) + 360) % 360;
  const raw = Math.abs(normalize(a) - normalize(b));
  return raw > 180 ? 360 - raw : raw;
}

export default haversineMeters;
